// Phase 5 — Admin CRUD for the methodology knowledge base.
// Staff-only. Content is markdown; reads used server-side to feed the AI prompt.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertStaff(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin") && !roles.includes("assistant")) throw new Error("Forbidden");
}
async function admin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

export const AGE_BANDS = ["12-24m", "2-4y", "4-6y", "6-8y", "8-10y"] as const;
export const KB_KINDS = [
  "definition",
  "weakness_signs",
  "activities",
  "parent_phrases",
  "parent_mistakes",
  "games_stories",
  "measurement",
  "prevention",
] as const;

export const listMethodologyKb = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    const [{ data: rows }, { data: pillars }] = await Promise.all([
      sb.from("methodology_kb").select("*").order("pillar_slug").order("age_band").order("kind"),
      sb.from("pillars").select("slug, name_ar").order("order", { ascending: true }),
    ]);
    return { rows: rows ?? [], pillars: pillars ?? [] };
  });

export const upsertMethodologyKb = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z.object({
      id: z.string().uuid().optional(),
      pillar_slug: z.string().min(1),
      age_band: z.enum(AGE_BANDS),
      kind: z.enum(KB_KINDS),
      content_md: z.string().default(""),
      is_active: z.boolean().default(true),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    if (data.id) {
      const { error } = await sb.from("methodology_kb").update({
        content_md: data.content_md,
        is_active: data.is_active,
      }).eq("id", data.id);
      if (error) throw error;
      return { id: data.id };
    }
    const { data: row, error } = await sb.from("methodology_kb").insert({
      pillar_slug: data.pillar_slug,
      age_band: data.age_band,
      kind: data.kind,
      content_md: data.content_md,
      is_active: data.is_active,
    }).select("id").single();
    if (error) throw error;
    return { id: row.id };
  });